import React, { useState, useEffect } from 'react';
import { IconMenu2 } from '@tabler/icons-react';
import { Drawer, Avatar, Box, IconButton, Typography, ListItemIcon } from '@mui/material';
import axios from 'axios';
import SideNavbar from 'components/sidebar';
import { getPathByName, iconHandler } from '../../utils';
import { useNavigate } from 'react-router-dom';
import { useProfile } from '../../custom-hooks';
import {
  AdminInfo,
  AdminName,
  AdminRole,
  DrawerStyle,
  MenuButton,
  Navbar,
  ProfileImageWrapper,
  StyledMenu,
  StyledMenuItem
} from './style';
import { colors, ICON_TYPE, ROLES } from '../../constants';
import i18n from '../../i18n';
import { REACT_APP_STRAPI_BASE_URL } from '../../config';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import 'moment/locale/de';
import 'moment/locale/en-gb'; // covers en-IN as well

interface Language {
  code: string;
  name: string;
}

const langLocales = [
  { key: 'English', value: 'en', label: 'English' },
  { key: 'German', value: 'de', label: 'Deutsch' },
  { key: 'Hindi', value: 'hi', label: 'हिंदी (Hindi)' },
  { key: 'Spanish', value: 'es', label: 'español' },
  { key: 'French', value: 'fr_FR', label: 'français' },
  { key: 'Arabic', value: 'ar', label: 'العربية (al-ʿArabiyya)' },
  { key: 'Russian', value: 'ru', label: 'русский язык (russkiy yazyk)' },
  { key: 'Portuguese', value: 'pt', label: 'Português' },
  { key: 'Japanese', value: 'ja-JP', label: '日本語 (Nihongo)' },
  { key: 'Korean', value: 'ko-KR', label: '한국어 (hangugeo)' }
];

const Header = () => {
  const navigate = useNavigate();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [availableLanguages, setAvailableLanguages] = useState<Language[]>([]);
  const [selectedLang, setSelectedLang] = useState<string>(
    localStorage.getItem('selectedLanguage') || i18n.language || 'en'
  );

  const userName = localStorage.getItem('userName') || '';
  const userRole = localStorage.getItem('userRole') || '';

  const { details } = useProfile();

  const handleDrawerOpen = () => setIsDrawerOpen(true);
  const handleDrawerClose = () => setIsDrawerOpen(false);

  const handleLangClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleLangClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (lang: string) => {
    i18n.changeLanguage(lang);
    moment.locale(lang);
    localStorage.setItem('selectedLanguage', lang);
    setSelectedLang(lang);
    handleLangClose();
  };

  // Fetch available languages from Strapi
  const fetchAvailableLanguages = async () => {
    try {
      const res = await axios.get(`${REACT_APP_STRAPI_BASE_URL}/i18n/locales`);
      const langs: Language[] = res.data.map((l: { code: string; name: string }) => ({
        code: l.code,
        name: l.name.replace(/\s*\(.*\)$/, '') // remove code in parentheses
      }));
      setAvailableLanguages(langs);
    } catch (err) {
      console.error('Failed to fetch languages:', err);
    }
  };

  // On mount, fetch languages and set selected language in i18n
  useEffect(() => {
    fetchAvailableLanguages();

    // Set i18n language on refresh
    const storedLang = localStorage.getItem('selectedLanguage');
    if (storedLang && storedLang !== i18n.language) {
      i18n.changeLanguage(storedLang);
      setSelectedLang(storedLang);
    }
  }, []);

  const languages = langLocales.filter((lang) =>
    availableLanguages.some((l) => l.code === lang.value)
  );

  const ROLE_KEYS: Record<string, string> = {
    'Super Admin': 'superAdmin',
    'Practice Admin': 'practiceAdmin',
    'Dept Admin': 'deptAdmin',
    Doctor: 'doctor',
    Other: 'other',
    Patient: 'patient'
  };
  const { t } = useTranslation();

  return (
    <>
      <Navbar>
        <MenuButton onClick={handleDrawerOpen}>
          <IconMenu2 />
        </MenuButton>

        {/* Language Dropdown */}

        <IconButton
          onClick={handleLangClick}
          color="primary"
          sx={{ right: '15rem', position: 'absolute' }}>
          {iconHandler(ICON_TYPE.LANGUAGE, colors.black, 1.2, 26)}
        </IconButton>

        <StyledMenu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleLangClose}
          elevation={0}>
          {languages.map((lang) => {
            const isSelected = lang.value === selectedLang;

            return (
              <StyledMenuItem
                key={lang.value}
                selected={isSelected}
                onClick={() => handleLanguageChange(lang.value)}>
                <ListItemIcon>
                  {iconHandler(
                    ICON_TYPE.LANGUAGE,
                    isSelected ? colors.blueBase : colors.black,
                    1.2,
                    20
                  )}
                </ListItemIcon>

                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                  flexGrow={1}
                  ml={1}>
                  <Typography
                    fontWeight={600}
                    fontSize={15}
                    color={isSelected ? colors.blueBase : colors.black}>
                    {lang.key}
                  </Typography>

                  <Typography
                    fontSize={14}
                    fontWeight={500}
                    color={isSelected ? colors.blueBase : colors.DarkGrayLight}>
                    {lang.label}
                  </Typography>
                </Box>
              </StyledMenuItem>
            );
          })}
        </StyledMenu>

        <AdminInfo>
          <AdminRole>{userName}</AdminRole>
          <AdminName> {t(`roles.${ROLE_KEYS[userRole] || 'other'}`)}</AdminName>
        </AdminInfo>

        <ProfileImageWrapper
          onClick={() =>
            navigate(
              getPathByName(
                userRole === ROLES.SUPER_ADMIN
                  ? 'superAdminProfile'
                  : userRole === ROLES.PRACTISE_ADMIN
                    ? 'practiseAdminProfile'
                    : ''
              ) as string
            )
          }>
          <Avatar
            alt="Admin"
            src={details?.image}
            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </ProfileImageWrapper>
      </Navbar>

      <Drawer anchor="left" open={isDrawerOpen} onClose={handleDrawerClose} sx={DrawerStyle}>
        <Box role="presentation" onClick={handleDrawerClose} onKeyDown={handleDrawerClose}>
          <SideNavbar />
        </Box>
      </Drawer>
    </>
  );
};

export default Header;
